<template>
    <b-modal
        :visible="true"
        no-close-on-esc
        no-close-on-backdrop
        no-fade
        size="lg"
        modal-class="search-modal"
        header-class="panel-header"
        footer-class="custom-footer"
        @close="$_close"
    >
        <!-- Header -->
        <template #modal-header="{ close }">
            <h3 class="panel-header-title">
                {{ $t('searchMetadata.search') }}
            </h3>
            <!-- Close icon -->
            <icon-close
                SMT-NG-CLOSE_SEARCH_METADATA_EDIT
                @close="close()"
                class="d-inline-block float-right"
            />
        </template>
        <!-- Search options -->
        <b-form v-on:submit.prevent="$_submit">
            <div class="search col-md-8">
                <x-input
                    name="searchUrl"
                    :text="$t('searchMetadata.url')"
                    :placeholder="$t('searchMetadata.url')"
                    v-model="searchUrl"
                    maxlength="150"
                    :required="false"
                />
                <x-input
                    name="searchSubject"
                    :text="$t('searchMetadata.subject')"
                    :placeholder="$t('searchMetadata.subject')"
                    v-model="searchSubject"
                    maxlength="50"
                    :required="false"
                />
                <b-form-group :label="$t('searchMetadata.searchIn')">
                    <b-form-radio-group
                        name="searchOptions"
                        :options="searchOptions"
                        v-model="selectedSearchOption"
                    >
                    </b-form-radio-group>
                </b-form-group>
                <b-button variant="primary" type="submit">{{
                    $t('btnSearchLabel')
                }}</b-button>
            </div>
        </b-form>
        <!-- Search results -->
        <div class="results">
            <!--Error-->
            <span v-if="error" class="error">{{
                `${$t('errorWhenSearching')}: ${error}`
            }}</span>
            <template v-if="showSearchResults">
                <div v-for="(record, index) in searchResult" :key="index">
                    <div
                        class="float-right"
                        v-if="
                            $_getThumbnailUri(record) &&
                            $_getThumbnailUri(record).length > 0
                        "
                    >
                        <img
                            class="record-img"
                            v-if="$_isImageVisible(index)"
                            :src="$_getThumbnailUri(record)"
                            :alt="$t('searchMetadata.resultImage')"
                            @error="() => $_handleError(index)"
                        />
                    </div>
                    <h3>
                        {{ record['dc:title'] }}
                    </h3>
                    <div>
                        <span class="font-weight-bold">Samenvatting: </span>
                        <span>
                            {{ $_getAbstract(record) }}
                        </span>
                    </div>
                    <div>
                        <span class="font-weight-bold">Soort: </span>
                        {{ record['dc:type'] }}
                    </div>
                    <div v-if="record['dc:date']">
                        <span class="font-weight-bold">Aangemaakt: </span>
                        {{ record['dc:date'] }}
                    </div>
                    <div v-if="$_getSubjectString(record)">
                        <span class="font-weight-bold">Onderwerpen: </span>
                        {{ $_getSubjectString(record) }}
                    </div>
                    <div>
                        <span class="font-weight-bold">Metadata URL: </span>
                        <b-link @click="$_copyUrl(record)">
                            {{ $t('searchMetadata.copyUrl') }}
                        </b-link>
                    </div>
                    <hr />
                </div>
                <div class="text-center">
                    <b-button
                        v-if="startPosition > 1"
                        variant="primary"
                        :disabled="startPosition === 1"
                        class="mr-2"
                        @click="$_previous"
                        >{{ $t('searchMetadata.previous') }}</b-button
                    >
                    <span v-if="numberOfRecordsMatched === 0">{{
                        $t('noResultsFound')
                    }}</span>
                    <span v-else class="pb-2">{{
                        $t('searchMetadata.results', [
                            `${Math.min(
                                parseInt(startPosition),
                                parseInt(numberOfRecordsMatched)
                            )}`,
                            `${Math.min(
                                parseInt(startPosition) + parseInt(PAGE_SIZE),
                                parseInt(numberOfRecordsMatched)
                            )}`,
                            `${numberOfRecordsMatched}`
                        ])
                    }}</span>
                    <b-button
                        v-if="nextRecord > 0"
                        variant="primary"
                        :disabled="nextRecord === 0"
                        class="ml-2"
                        @click="$_next"
                        >{{ $t('searchMetadata.next') }}</b-button
                    >
                </div>
            </template>
        </div>
        <!-- Footer -->
        <template #modal-footer>
            <!-- Buttons -->
            <b-button @click="$_close">
                {{ $t('btnCloseLabel') }}
            </b-button>
        </template>
    </b-modal>
</template>

<script>
    import axios from 'axios';
    import xml2js from 'xml2js';

    import XInput from '../../components/BaseComponents/x-input.vue';
    import IconClose from '../../components/IconClose.vue';
    import {
        createOcwQuery,
        nationaalGeoRegisterCSWUrl
    } from '../../services/utilities/apis/csw';

    const BASE_URL_RECORDS =
        'https://nationaalgeoregister.nl/geonetwork/srv/api/records';
    const PAGE_SIZE = 10;
    const MAX_STRING_LENGTH = 350;

    export default {
        components: {
            'icon-close': IconClose,
            'x-input': XInput
        },
        props: { defaultUrl: { type: String, required: false, default: '' } },
        mounted() {
            this.searchUrl = this.defaultUrl;
            this.$_search();
        },
        data() {
            return {
                BASE_URL_RECORDS: BASE_URL_RECORDS,
                PAGE_SIZE: PAGE_SIZE,
                MAX_STRING_LENGTH: MAX_STRING_LENGTH,
                startPosition: 1,
                nextRecord: 0,
                numberOfRecordsMatched: 0,
                error: '',
                searchUrl: '',
                searchSubject: '',
                showSearchResults: false,
                searchResult: [],
                selectedSearchOption: 'dataset',
                searchOptions: [
                    { value: 'dataset', text: this.$t('searchMetadata.dataset') },
                    { value: 'service', text: this.$t('searchMetadata.services') },
                    {
                        value: 'all',
                        text: this.$t('searchMetadata.datasetAndServices')
                    }
                ]
            };
        },
        computed: {
            typeSearch() {
                if (this.selectedSearchOption === 'all') {
                    return null;
                } else {
                    return this.selectedSearchOption;
                }
            },
            cswQuery() {
                return createOcwQuery(
                    this.searchUrl,
                    this.searchSubject,
                    this.typeSearch,
                    this.PAGE_SIZE,
                    this.startPosition
                );
            }
        },
        methods: {
            $_close() {
                this.$emit('close');
            },
            $_submit() {
                this.$_reset();
                this.$_search();
            },
            // If loading the image results in an error, hide the image so we don't see broken images on the page.
            $_handleError(index) {
                this.$set(this.searchResult[index], 'isImageVisible', false); // Hide the image for the specific record
            },
            $_isImageVisible(index) {
                // Check if isImageVisible exists and if it's false
                const isVisible = this.searchResult[index]?.isImageVisible;
                return isVisible !== false; // Returns true if undefined or true, false if explicitly false
            },
            async $_search() {
                try {
                    const response = await axios.post(
                        nationaalGeoRegisterCSWUrl,
                        this.cswQuery,
                        {
                            headers: {
                                'Content-Type': 'application/xml',
                                Accept: 'application/xml'
                            }
                        }
                    );

                    // handle success
                    const jsonResult = await this.$_xmlToJson(response.data);

                    this.nextRecord =
                        jsonResult['csw:GetRecordsResponse']['csw:SearchResults'][
                            '$'
                        ]['nextRecord'];
                    this.numberOfRecordsMatched =
                        jsonResult['csw:GetRecordsResponse']['csw:SearchResults'][
                            '$'
                        ]['numberOfRecordsMatched'];
                    const records =
                        jsonResult['csw:GetRecordsResponse']['csw:SearchResults'][
                            'csw:Record'
                        ];

                    this.searchResult = [];
                    if (this.numberOfRecordsMatched > 0) {
                        // Make sure searchresult is an Array, even if there is just one searchresult.
                        this.searchResult = Array.isArray(records)
                            ? records
                            : [records];
                    }

                    this.showSearchResults = true;
                } catch (error) {
                    // handle error
                    this.error = error;
                    console.log('Searching URL failed: ', error);
                }
            },
            $_previous() {
                if (this.startPosition > 1) {
                    this.startPosition = this.startPosition - PAGE_SIZE;

                    if (this.startPosition < 1) {
                        this.startPosition = 1;
                    }
                }
                this.showSearchResults = false;
                this.$_search();
            },
            $_next() {
                if (this.nextRecord > 0) {
                    this.startPosition = this.nextRecord;
                }
                this.showSearchResults = false;
                this.$_search();
            },
            $_reset() {
                this.error = '';
                this.searchResult = '';
                this.showSearchResults = false;
                this.startPosition = 1;
                this.numberOfRecordsMatched = 0;
            },
            $_copyUrl(record) {
                const url = `${BASE_URL_RECORDS}/${record['dc:identifier']}/formatters/xml`;
                this.$emit('updateUrl', url);
                this.$_close();
            },
            $_xmlToJson(xml) {
                return new Promise((resolve, reject) => {
                    const parser = new xml2js.Parser({
                        explicitArray: false,
                        trim: true
                    });

                    parser.parseString(xml, (err, result) => {
                        if (err) {
                            reject(err);
                        } else {
                            resolve(result);
                        }
                    });
                });
            },
            $_getThumbnailUri(record) {
                // The XML contains URI's with name attribute
                // <dc:URI name="Thumbnail">URL</dc:URI>
                //
                // As an exception to the rule some URI's do not have a name attribute
                // <dc:URI>URL</dc:URI>
                //
                // Here we retrieve the URI with name thumbnail to show a picture in the searchresult page
                const uris = record['dc:URI'];
                if (uris && uris.length > 0) {
                    try {
                        const thumbnailItem = uris.filter(
                            (item) => item.$.name === 'thumbnail'
                        );

                        // Check if the thumbnailItem array has any items and extract the `_` property
                        const thumbnailUrl =
                            thumbnailItem.length > 0 ? thumbnailItem[0]._ : null;
                        return thumbnailUrl;
                    } catch (error) {
                        // We get here when the URI does not contain a Name attribute.
                        return null;
                    }
                }
                return null;
            },
            $_getAbstract(record) {
                let abstract = record['dct:abstract'];
                if (abstract) {
                    return abstract.length > MAX_STRING_LENGTH
                        ? abstract.substring(0, MAX_STRING_LENGTH)
                        : abstract;
                }
                return null;
            },
            $_getSubjectString(record) {
                if (record['dc:subject'] && ['dc:subject'].length > 0) {
                    return record['dc:subject'].join(', ');
                } else {
                    return null;
                }
            }
        }
    };
</script>

<style lang="scss">
.search-modal .modal-dialog {
    & .panel-header {
        border-top-left-radius: 0;
        border-top-right-radius: 0;
    }

    & .custom-footer {
        border: none;
    }

    & .modal-content {
        height: 100%;
        border: none;
        border-radius: initial;
    }
}

.search-modal .modal-body {
    padding: 0px;
    height: 100%;
}

@media (max-width: 767px) {
    .search-modal .modal-dialog {
        top: 0 !important;
        left: 0%;
        bottom: 0 !important;
        right: 0;
        min-width: 0;
        max-width: none;
    }
}
</style>

<style scoped lang="scss">
.search {
    padding: 20px 20px 0px 20px;
}

.results {
    display: flex !important;
    flex-direction: column;
    overflow-y: auto;
    padding: 20px;
    flex: 1;

    & .record-img {
        max-width: 100px;
        max-height: 60px;
    }
}

.error {
    color: red;
}
</style>
